// src/pages/Customers/components/CustomerDetailModal.tsx
import React from "react";
import {Dialog, Transition} from "@headlessui/react";
import {Fragment} from "react";
import {
  X,
  User,
  Mail,
  Phone,
  Calendar,
  UserCheck,
  UserX,
  Heart,
  MessageSquare,
  Home,
  Clock,
  Activity,
} from "lucide-react";
import type {UserResponseDto} from "../../../types/users";
import {formatDate} from "../../../utils/formatters";
import Tabs from "../../../components/common/Tab";
import WishlistTab from "./WishlistTab";

interface CustomerDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer?: UserResponseDto | null;
}

// Badge Component
const Badge: React.FC<{
  children: React.ReactNode;
  variant: "success" | "warning" | "danger" | "info" | "gray";
}> = ({children, variant}) => {
  const variants = {
    success:
      "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
    warning:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
    danger: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
    info: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
    gray: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[variant]}`}
    >
      {children}
    </span>
  );
};

// Info Row Component
const InfoRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}> = ({icon, label, value}) => (
  <div className="flex items-start space-x-3 py-3">
    <div className="flex-shrink-0 text-gray-400">{icon}</div>
    <div className="flex-1">
      <div className="text-sm font-medium text-gray-900 dark:text-white">
        {label}
      </div>
      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
        {value}
      </div>
    </div>
  </div>
);

// Stats Card Component
const StatsCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
}> = ({icon, label, value, color}) => (
  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
    <div className="flex items-center space-x-3">
      <div className={`p-2 rounded-lg ${color}`}>{icon}</div>
      <div>
        <div className="text-lg font-semibold text-gray-900 dark:text-white">
          {value.toLocaleString()}
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">{label}</div>
      </div>
    </div>
  </div>
);

const CustomerDetailModal: React.FC<CustomerDetailModalProps> = ({
  isOpen,
  onClose,
  customer,
}) => {
  if (!customer) return null;

  const hasActivity =
    (customer.propertyCount || 0) > 0 ||
    (customer.favoriteCount || 0) > 0 ||
    (customer.inquiryCount || 0) > 0;

  const tabs = [
    {
      label: "Details",
      content: (
        <div className="space-y-6">
          {/* Customer Profile */}
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              {customer.avatar ? (
                <img
                  className="h-16 w-16 rounded-full object-cover"
                  src={customer.avatar}
                  alt={customer.name}
                />
              ) : (
                <div className="h-16 w-16 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                  <span className="text-xl font-medium text-gray-700 dark:text-gray-300">
                    {customer.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {customer.name}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                Customer ID: {customer.id}
              </p>
              <div className="flex items-center space-x-2">
                <Badge variant="gray">
                  <User className="w-3 h-3 mr-1" />
                  Customer
                </Badge>
                <Badge
                  variant={customer.isVerified ? "success" : "warning"}
                >
                  {customer.isVerified ? (
                    <UserCheck className="w-3 h-3 mr-1" />
                  ) : (
                    <UserX className="w-3 h-3 mr-1" />
                  )}
                  {customer.isVerified ? "Verified" : "Unverified"}
                </Badge>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
              Contact Information
            </h4>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              <InfoRow
                icon={<Mail className="w-4 h-4" />}
                label="Email Address"
                value={
                  <div className="flex items-center space-x-2">
                    <span>{customer.email}</span>
                    {customer.emailVerifiedAt && (
                      <span className="text-xs text-green-600 dark:text-green-400">
                        (Verified on{" "}
                        {formatDate(customer.emailVerifiedAt)})
                      </span>
                    )}
                  </div>
                }
              />
              <InfoRow
                icon={<Phone className="w-4 h-4" />}
                label="Phone Number"
                value={customer.phone || "Not provided"}
              />
            </div>
          </div>

          {/* Activity Statistics */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
              Activity Overview
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <StatsCard
                icon={<Home className="w-4 h-4 text-blue-600" />}
                label="Properties"
                value={customer.propertyCount || 0}
                color="bg-blue-50 dark:bg-blue-900/20"
              />
              <StatsCard
                icon={<Heart className="w-4 h-4 text-red-600" />}
                label="Favorites"
                value={customer.favoriteCount || 0}
                color="bg-red-50 dark:bg-red-900/20"
              />
              <StatsCard
                icon={
                  <MessageSquare className="w-4 h-4 text-green-600" />
                }
                label="Inquiries"
                value={customer.inquiryCount || 0}
                color="bg-green-50 dark:bg-green-900/20"
              />
            </div>
          </div>

          {/* Account Information */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
              Account Information
            </h4>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              <InfoRow
                icon={<Calendar className="w-4 h-4" />}
                label="Member Since"
                value={formatDate(customer.createdAt)}
              />
              <InfoRow
                icon={<Clock className="w-4 h-4" />}
                label="Last Login"
                value={
                  customer.lastLogin
                    ? formatDate(customer.lastLogin)
                    : "Never logged in"
                }
              />
              <InfoRow
                icon={<Calendar className="w-4 h-4" />}
                label="Last Updated"
                value={formatDate(customer.updatedAt)}
              />
            </div>
          </div>

          {/* Activity Summary */}
          {hasActivity ? (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Activity className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h5 className="text-sm font-medium text-blue-900 dark:text-blue-300">
                    Customer Activity Summary
                  </h5>
                  <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                    This customer has been active on the platform with{" "}
                    {customer.propertyCount || 0} properties listed,{" "}
                    {customer.favoriteCount || 0} favorites saved, and{" "}
                    {customer.inquiryCount || 0} inquiries made.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <User className="w-5 h-5 text-gray-500 mt-0.5" />
                <div>
                  <h5 className="text-sm font-medium text-gray-900 dark:text-gray-300">
                    New Customer
                  </h5>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    This customer hasn't started using the platform
                    features yet.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      label: "Wishlist",
      content: <WishlistTab customerId={customer.id} />,
    },
  ];

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                  Customer Details
                </Dialog.Title>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                <Tabs tabs={tabs} />
              </div>

              {/* Footer */}
              <div className="flex justify-end px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Close
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CustomerDetailModal;
